import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import CaretRight from '@/components/Icons/CaretRight';
import CheckCircle from '@/components/Icons/CheckCircle';
import Integration from '@/components/Icons/Integration';
import ListBullets from '@/components/Icons/ListBullets';
import TaskEfficiencyIndicator from '@/components/TaskEfficiencyIndicator';
import * as format from '@/format';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import { useUser } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import UserProfileContext from '@/layouts/UserProfileLayout/UserProfileContext';
import SkipTaskConfirmationModal from '@/modals/SkipTaskConfirmationModal';
import { useOpenFormRequestPane } from '@/panes/hooks';
import * as tsClient from '@/tsClient';
import {
  type Creator,
  FormProviders,
  type FormRequest,
  type FormRequestTemplate,
  FormStatus,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES,
  type Person,
} from '@/typings/api-models';

import Tooltip from '../Tooltip';

import s from './FormRequestCard.module.scss';

type Props = {
  creator: Pick<Creator, 'slug'>;
  request: Pick<FormRequest, 'id' | 'url' | 'status'> & {
    template: Pick<FormRequestTemplate, 'id' | 'name' | 'provider'>;
    person: Pick<Person, 'id'>;
  };
  canClick: boolean;
  canSkip: boolean;
  setIsFulfilled?: (b: boolean) => void;
  setIsSkipped?: (b: boolean) => void;
  onChange?: () => void;
  completedAt?: string | null;
  integrationSlugs?: string[];
};

const FormRequestCard: React.FC<Props> = ({
  setIsFulfilled,
  setIsSkipped,
  onChange,
  canClick,
  canSkip,
  creator,
  request,
  completedAt,
  integrationSlugs,
}) => {
  const router = useRouter();
  const [user] = useUser();
  const openFormRequestPane = useOpenFormRequestPane();

  const [skipTaskModalOpen, setSkipTaskModalOpen] = useState(false);
  const { creator: creatorFromContext, isInDashboard } = useContext(DashboardContext);
  const { isInUserProfile } = useContext(UserProfileContext);

  const { getData: updateFormRequest } = useNoodleApi(tsClient.formRequests.updateFormRequest, {
    toastOnError: true,
  });

  const isCompleted = request.status === FormStatus.COMPLETED;
  const isSkipped = request.status === FormStatus.SKIPPED;
  const isSubmittedForReview = request.status === FormStatus.SUBMITTED_FOR_REVIEW;
  useEffect(() => {
    setIsFulfilled?.(isCompleted);
  }, [isCompleted, setIsFulfilled]);

  useEffect(() => {
    setIsSkipped?.(isSkipped);
  }, [isSkipped, setIsSkipped]);

  const handleClick = (): void => {
    if (isSkipped) {
      return;
    }
    if (isInDashboard) {
      if (typeof router.query.userWorkflowId === 'string') {
        window.open(
          getUrl(IDENTIFIERS.DASHBOARD_FORM_REQUEST_DETAILS, {
            formRequestId: request.id,
            userWorkflowId: router.query.userWorkflowId,
          }),
          '_blank',
        );
      } else {
        openFormRequestPane({ formRequestId: request.id });
      }
    } else if (
      user &&
      request.person.id === user.id &&
      !isCompleted &&
      request.template.provider === FormProviders.Typeform
    ) {
      window.open(request.url, '_blank');
    } else if (isInUserProfile) {
      openFormRequestPane({ formRequestId: request.id });
    } else {
      router.push(getUrl(IDENTIFIERS.FORM_REQUEST_DETAILS, { creatorSlug: creator.slug, formRequestId: request.id }));
    }
  };

  const handleSkipTask = async (): Promise<void> => {
    await updateFormRequest({
      formRequestId: request.id,
      status: FormStatus.SKIPPED,
    });
    setSkipTaskModalOpen(false);
    if (onChange) onChange();
  };

  const handleUnskipTask = async (): Promise<void> => {
    await updateFormRequest({
      formRequestId: request.id,
      skippedAt: null,
    });
    if (onChange) onChange();
  };

  return (
    <>
      {skipTaskModalOpen && (
        <SkipTaskConfirmationModal
          onClose={() => setSkipTaskModalOpen(false)}
          onConfirm={handleSkipTask}
          taskType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST}
        />
      )}
      <Buttons
        className={s.container}
        isFullWidth
        isWrapper
        key={request.id}
        onClick={handleClick}
        disabled={!canClick || (isSkipped && !canSkip)}
        style={isSkipped ? { backgroundColor: 'var(--color-gray-25)', borderRadius: 8 } : {}}
      >
        {isCompleted ? (
          <CheckCircle size={16} weight='fill' color='var(--color-success)' />
        ) : (
          <figure>
            <ListBullets size={20} color='var(--color-gray-100)' />
          </figure>
        )}
        <div>
          <p
            className={isCompleted ? 'caption-bold' : 'body-sm-bold'}
            style={isCompleted ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
          >
            {request.template.name}
          </p>
          {isSubmittedForReview && creatorFromContext && isInDashboard && (
            <p className='caption'>
              <strong style={{ color: 'var(--color-warning)' }}>Review required by you</strong>
            </p>
          )}
          {isSubmittedForReview && isInUserProfile && (
            <>
              <p className='caption' style={{ color: 'var(--color-gray-75)' }}>
                Your submission is being reviewed
              </p>
            </>
          )}
          {((completedAt && isCompleted) || (isInDashboard && (integrationSlugs?.length || 0) > 0)) && (
            <div className={s.type}>
              {completedAt && isCompleted && (
                <p className='caption' style={{ color: 'var(--color-gray-75)' }}>
                  Completed on {format.datetimeLocalTZ.withoutTimezone({ datetime: completedAt })}
                </p>
              )}
              {isInDashboard && (integrationSlugs?.length || 0) > 0 && (
                <Tooltip
                  text={`Data in this form is going to eFile with the ${format.csl(integrationSlugs?.map((slug) => slug.toUpperCase()) || [])}`}
                >
                  <Integration weight='fill' color='var(--color-noodle)' size={12} />
                </Tooltip>
              )}
            </div>
          )}
          {isSkipped && <p className='caption'>This task is no longer required.</p>}
        </div>
        {isSkipped && isInDashboard && (
          <Buttons
            isWrapper
            onClick={(e) => {
              e.stopPropagation();
              handleUnskipTask();
            }}
          >
            <div className={s.open}>Unskip</div>
          </Buttons>
        )}
        {canClick && (!isSkipped || canSkip) && (
          <>
            {isCompleted ? (
              <CaretRight size={12} color='var(--color-gray-100)' />
            ) : (
              <div
                className={s.open}
                {...(!isCompleted &&
                  !isInDashboard && {
                    style: {
                      backgroundColor: 'var(--color-primary)',
                      border: '1px solid var(--color-primary)',
                      color: 'var(--color-gray-0)',
                    },
                  })}
              >
                View
              </div>
            )}
          </>
        )}
      </Buttons>
      <TaskEfficiencyIndicator
        className='px-3 py-1'
        referenceType='form_request'
        referenceId={request.id}
        isSkipped={isSkipped}
      />
      {canSkip && !isSkipped && (
        <Buttons
          onClick={(event) => {
            event.stopPropagation();
            setSkipTaskModalOpen(true);
          }}
          className={s.skip}
        >
          Skip
        </Buttons>
      )}
    </>
  );
};

export default FormRequestCard;
